import React from "react";
import { motion } from "framer-motion";
import { FiCheckCircle } from "react-icons/fi";
import SectionHeading from "../../../components/SectionHeading";
import { outcomeLabel, stepsContent } from "./certificationContent";

const stepVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

/* The six steps as rows: illustration on the left, the step's copy on the
   right, the SAME WAY ROUND for every step. They alternated sides at first;
   that was dropped on the user's instruction (2026-09-13) so the column of
   illustrations lines up down the page. Below 1024 every row stacks to
   illustration-then-copy.

   The illustrations are the six already in public/assets/certification-process,
   the same set the homepage process conveyor uses. */
const CertificationSteps = () => (
  <section
    className="bg-white py-14 lg:py-20"
    aria-labelledby="certification-steps-heading"
  >
    <div className="brand-col">
      <SectionHeading
        id="certification-steps-heading"
        title={stepsContent.title}
        subtitle={stepsContent.subtitle}
      />
      <ol className="list-none space-y-8 p-0 lg:space-y-12">
        {stepsContent.steps.map((step) => (
          <motion.li
            key={step.number}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={stepVariants}
            className="brand-card h-auto overflow-hidden"
          >
            <div className="grid grid-cols-1 items-center gap-6 p-6 lg:grid-cols-[18rem_1fr] lg:gap-10 lg:p-8">
              {/* the illustration column, first on every row */}
              <div className="flex items-center justify-center rounded-xl bg-[rgba(104,46,146,0.05)] p-4">
                <img
                  src={step.image}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="h-40 w-auto max-w-full object-contain lg:h-48"
                />
              </div>

              <div className="min-w-0">
                <h3 className="text-lg lg:text-xl font-bold text-gray-900">
                  {step.title}
                </h3>
                <p className="mt-1 text-sm md:text-base font-semibold text-[var(--brand-primary)]">
                  {step.subtitle}
                </p>
                <p className="mt-4 text-sm md:text-base leading-relaxed text-gray-600">
                  {step.description}
                </p>

                <p className="mt-5 text-sm font-semibold text-gray-900">
                  {step.listLabel}
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                  {step.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <FiCheckCircle
                        aria-hidden="true"
                        size={18}
                        className="mt-0.5 flex-shrink-0 text-[var(--brand-primary)]"
                      />
                      <span className="text-sm md:text-base text-gray-700">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>

                {step.note && (
                  <p className="mt-4 text-sm md:text-base leading-relaxed text-gray-600">
                    {step.note}
                  </p>
                )}

                <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-gray-900">
                    {outcomeLabel}
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-2">
                    {step.outcomes.map((outcome) => (
                      <li key={outcome} className="flex items-start gap-2">
                        <FiCheckCircle
                          aria-hidden="true"
                          size={16}
                          className="mt-0.5 flex-shrink-0 text-[var(--brand-orange)]"
                        />
                        <span className="text-sm text-gray-700">{outcome}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.li>
        ))}
      </ol>
    </div>
  </section>
);

export default CertificationSteps;
